import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { API_PATH } from 'src/environments/environment';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { AiHelpEvent, AiHelpMessage } from './ai-help.models';

/**
 * Cliente do assistente de ajuda (SSE via fetch).
 *
 * Por que fetch e não HttpClient: o Angular HttpClient não expõe streaming de
 * texto incremental de forma nativa; o assistente precisa renderizar a resposta
 * em streaming. Usamos fetch() + ReadableStream e montamos os headers de
 * autenticação (Authorization + x-tenant-id) manualmente a partir do
 * AuthService — a chave da IA vive SÓ no backend, nunca aqui.
 *
 * Portado do abcx-web (mesmo contrato SSE, `POST /ai-help/chat`). O Core é
 * multi-tenant como o ABCX, então o header `x-tenant-id` também é enviado
 * (lido do AuthService.getTenantId(), que decodifica o JWT igual ao ABCX).
 *
 * Componente/serviço isolados de propósito (sem dependência do CRUD do ERP)
 * para poderem ser reaproveitados por outros shells.
 */
@Injectable()
export class AiHelpService {
  constructor(
    private readonly authService: AuthService,
    private readonly zone: NgZone,
  ) {}

  /**
   * Abre o stream do chat. Emite cada evento SSE parseado. Complete ao receber
   * `done` (ou ao fim do corpo). O unsubscribe aborta a requisição.
   */
  streamChat(params: {
    question: string;
    screenContext?: string;
    history?: Array<Pick<AiHelpMessage, 'role' | 'content'>>;
  }): Observable<AiHelpEvent> {
    return new Observable<AiHelpEvent>((subscriber) => {
      const controller = new AbortController();

      const token = this.authService.getAuthorizationToken?.() ?? null;
      const tenantId = this.authService.getTenantId?.() ?? null;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantId) headers['x-tenant-id'] = tenantId;

      const emit = (event: AiHelpEvent) =>
        this.zone.run(() => subscriber.next(event));

      fetch(`${API_PATH}ai-help/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          question: params.question,
          screenContext: params.screenContext,
          history: params.history,
        }),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok || !response.body) {
            this.zone.run(() =>
              subscriber.error(new Error(`ai-help HTTP ${response.status}`)),
            );
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let doneReceived = false;

          // Um chunk pode conter vários eventos `data: {...}\n\n` — acumulamos
          // e cortamos por delimitador de evento SSE.
          // eslint-disable-next-line no-constant-condition
          outer: while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            // Normaliza CRLF -> LF no buffer COMPLETO (não só no chunk que
            // acabou de chegar): alguns proxies/backends emitem `\r\n\r\n`
            // entre eventos SSE, e um `\r\n` pode ficar dividido entre dois
            // chunks (`\r` no fim de um, `\n` no início do próximo) — normalizar
            // só o chunk novo nunca casaria esse par (achado do CodeRabbit no
            // PR #70).
            buffer = buffer.replace(/\r\n/g, '\n');

            let sep: number;
            while ((sep = buffer.indexOf('\n\n')) !== -1) {
              const rawEvent = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              const parsed = this.parseSseData(rawEvent);
              if (parsed) {
                emit(parsed);
                if (parsed.type === 'done') {
                  // Encerra assim que `done` chega, sem esperar o corpo
                  // fechar — o backend pode manter a conexão em keep-alive
                  // (achado do CodeRabbit no PR #70).
                  doneReceived = true;
                  break outer;
                }
              }
            }
          }

          if (doneReceived) {
            await reader.cancel().catch(() => undefined);
          } else {
            // resto do buffer (caso o backend não termine com \n\n)
            const tail = this.parseSseData(buffer);
            if (tail) emit(tail);
          }

          this.zone.run(() => subscriber.complete());
        })
        .catch((err) => {
          if (controller.signal.aborted) {
            this.zone.run(() => subscriber.complete());
            return;
          }
          this.zone.run(() => subscriber.error(err));
        });

      return () => controller.abort();
    });
  }

  /** Extrai o JSON das linhas `data:` de um bloco de evento SSE. */
  private parseSseData(rawEvent: string): AiHelpEvent | null {
    const dataLines = rawEvent
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim());

    if (dataLines.length === 0) return null;

    const payload = dataLines.join('\n');
    if (!payload) return null;

    try {
      return JSON.parse(payload) as AiHelpEvent;
    } catch {
      return null;
    }
  }
}
